import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { DEPARTMENT, ROLE_LEVEL } from '../../../core/auth/access-control';
import { AuthService } from '../../../core/auth/auth.service';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import { SalesDashboard, SalesDashboardService } from '../services/sales-dashboard.service';

@Component({
  selector: 'app-sales-dashboard',
  imports: [
    DecimalPipe,
    RouterLink,
    LucideAngularModule,
    StatusBadge,
    RefreshButtonComponent,
  ],
  templateUrl: './sales-dashboard.component.html',
})
export class SalesDashboardComponent implements OnInit {
  private readonly dashboardService = inject(SalesDashboardService);
  private readonly overlay = inject(GlobalOverlayService);
  readonly auth = inject(AuthService);
  readonly dashboard = signal<SalesDashboard | null>(null);
  readonly isLoading = signal(false);
  readonly summary = computed(() => this.dashboard()?.summary);
  readonly recentBookings = computed(() => this.dashboard()?.recentBookings ?? []);
  readonly salesTotal = computed(() => this.summary()?.netSales ?? 0);
  readonly outstandingTotal = computed(() => this.summary()?.outstandingAmount ?? 0);
  readonly collectedTotal = computed(() => Math.max(this.salesTotal() - this.outstandingTotal(), 0));
  readonly collectionPercent = computed(() =>
    this.salesTotal() ? Math.min((this.collectedTotal() / this.salesTotal()) * 100, 100) : 0,
  );
  readonly monthLabel = computed(() => {
    const period = this.dashboard()?.period;
    if (!period) return 'เดือนปัจจุบัน';
    return new Intl.DateTimeFormat('th-TH', { month: 'long', year: 'numeric', timeZone: 'Asia/Bangkok' })
      .format(new Date(Date.UTC(period.year, period.month - 1, 1)));
  });
  readonly canViewApprovalTasks = computed(() => {
    const user = this.auth.user();
    return (
      user?.role.level === ROLE_LEVEL.ADMIN ||
      (user?.department.code === DEPARTMENT.SALES && user.role.level === ROLE_LEVEL.MANAGER)
    );
  });

  ngOnInit(): void {
    void this.loadDashboard();
  }

  async loadDashboard(): Promise<void> {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.overlay.showLoading('กำลังโหลดข้อมูล Dashboard...');
    try {
      this.dashboard.set(await this.dashboardService.getDashboard());
    } catch (error: unknown) {
      const message =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : 'กรุณาลองใหม่อีกครั้ง';
      this.overlay.error('โหลดข้อมูล Dashboard ไม่สำเร็จ', message);
    } finally {
      this.isLoading.set(false);
      this.overlay.hideLoading();
    }
  }
}
